import React, { useCallback, useMemo, useState } from 'react';
import {
    WxbButton,
    WxbEmpty,
    WxbIcon,
    WxbModal,
    WxbSelect,
    wxbToast,
} from '../wxb-ui';
import { batchPlanApi } from '../../services/api';
import type { BatchPlan, BatchTemplateSummary } from '../../types';

interface BulkReplaceTemplateModalProps {
    visible: boolean;
    batches: BatchPlan[];           // only DRAFT batches
    templates: BatchTemplateSummary[];
    onClose: () => void;
    onApplied: () => void;
}

const BulkReplaceTemplateModal: React.FC<BulkReplaceTemplateModalProps> = ({
    visible,
    batches,
    templates,
    onClose,
    onApplied,
}) => {
    const [targetTemplateId, setTargetTemplateId] = useState<number | null>(null);
    const [applying, setApplying] = useState(false);
    const [applyProgress, setApplyProgress] = useState(0);

    const targetTemplate = useMemo(
        () => templates.find((t) => t.id === targetTemplateId) ?? null,
        [templates, targetTemplateId],
    );

    const handleClose = useCallback(() => {
        setTargetTemplateId(null);
        setApplyProgress(0);
        onClose();
    }, [onClose]);

    const handleApply = useCallback(async () => {
        if (!targetTemplateId || batches.length === 0) return;
        setApplying(true);
        setApplyProgress(0);

        const results: Array<{ ok: true } | { ok: false; error: any }> = [];

        for (const batch of batches) {
            try {
                await batchPlanApi.update(batch.id, {
                    batch_code: batch.batch_code,
                    batch_name: batch.batch_name,
                    template_id: targetTemplateId,
                    day0_date: batch.day0_date ?? batch.planned_start_date,
                    project_code: batch.project_code ?? null,
                    description: batch.description ?? null,
                    notes: batch.notes ?? null,
                    force_rebuild: true,
                });
                results.push({ ok: true });
            } catch (error) {
                results.push({ ok: false, error });
            } finally {
                setApplyProgress((current) => current + 1);
            }
        }

        setApplying(false);

        const succeeded = results.filter((r) => r.ok).length;
        const failed = results.length - succeeded;
        if (failed === 0) {
            wxbToast.success(`已批量替换模版：${succeeded} 个批次`);
        } else {
            const firstErr = results.find((r): r is { ok: false; error: any } => !r.ok)
                ?.error?.response?.data?.error;
            wxbToast.warning(`${succeeded} 个成功，${failed} 个失败${firstErr ? `：${firstErr}` : ''}`);
        }

        setTargetTemplateId(null);
        setApplyProgress(0);
        onApplied();
    }, [batches, targetTemplateId, onApplied]);

    const footer = (
        <div className="batch-modal-v4__replace-footer">
            <WxbButton variant="ghost" onClick={handleClose} type="button" disabled={applying}>
                取消
            </WxbButton>
            <WxbButton
                variant="primary"
                onClick={handleApply}
                disabled={!targetTemplateId || batches.length === 0 || applying}
                type="button"
            >
                {applying ? `替换中 ${applyProgress}/${batches.length}` : `替换模版 (${batches.length} 个批次)`}
            </WxbButton>
        </div>
    );

    return (
        <WxbModal
            open={visible}
            title="批量替换模版"
            onCancel={handleClose}
            footer={footer}
            width={560}
            centered
        >
            <div className="batch-modal-v4__replace-body">
                <div className="batch-modal-v4__replace-field">
                    <WxbSelect
                        label="选择新模版"
                        placeholder="请选择目标模版"
                        value={targetTemplateId ?? undefined}
                        onChange={(v) => setTargetTemplateId(v as number)}
                        options={templates.map((t) => ({
                            label: t.template_name ?? t.template_code,
                            value: t.id,
                        }))}
                        showSearch
                        filterOption={(input, option) =>
                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        className="batch-modal-v4__replace-template-select"
                    />
                </div>

                <div className="batch-modal-v4__replace-warning">
                    <WxbIcon className="batch-modal-v4__replace-warning-icon" name="warning" size={14} />
                    <span>
                        替换模版会清空所选批次的现有工序计划，并按新模版重新生成。此操作仅适用于草稿批次，不可撤销。
                    </span>
                </div>

                {batches.length === 0 ? (
                    <WxbEmpty description="没有可操作的草稿批次" />
                ) : (
                    <div className="batch-modal-v4__replace-list-wrap">
                        <div className="batch-modal-v4__replace-list-title">
                            将替换以下 {batches.length} 个草稿批次
                        </div>
                        {batches.map((batch) => (
                            <div
                                key={batch.id}
                                className="batch-modal-v4__replace-row"
                            >
                                <WxbIcon className="batch-modal-v4__replace-row-icon" name="batch-record" size={15} />
                                <div className="batch-modal-v4__replace-row-main">
                                    <div className="batch-modal-v4__replace-row-code">
                                        {batch.batch_code}
                                    </div>
                                    <div className="batch-modal-v4__replace-row-template">
                                        {batch.template_name ?? '未知模版'}
                                        {targetTemplate && (
                                            <>
                                                <span className="batch-modal-v4__replace-arrow">-&gt;</span>
                                                <span className="batch-modal-v4__replace-target">{targetTemplate.template_name ?? targetTemplate.template_code}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {batch.day0_date && (
                                    <div className="batch-modal-v4__replace-day0">
                                        Day0 {batch.day0_date}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </WxbModal>
    );
};

export default BulkReplaceTemplateModal;
